import React, { useEffect, useState } from "react";
import {
  getProfileImage,
  getProfileImageByCurp,
} from "../../services/profileImageCache.ts";

type BossProfileAvatarProps = {
  partnerId?: number;
  curp?: string | null;
  avatarUrl?: string | null;
  firstName?: string;
  lastName?: string;
  size?: number;
};

const BossProfileAvatar = ({
  partnerId,
  curp,
  avatarUrl,
  firstName = "",
  lastName = "",
  size = 52,
}: BossProfileAvatarProps) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setImgError(false);

    (async () => {
      try {
        const preset = avatarUrl?.trim();
        if (preset) {
          if (!cancelled) setUrl(preset);
          return;
        }

        let imageUrl: string | null = null;
        if (curp) {
          imageUrl = await getProfileImageByCurp(curp);
        } else if (partnerId) {
          imageUrl = await getProfileImage(partnerId);
        }
        if (!cancelled) setUrl(imageUrl);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [partnerId, curp, avatarUrl]);

  const initials = `${firstName?.charAt(0) || ""}${
    lastName?.charAt(0) || ""
  }`.toUpperCase();

  const boxStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: "50%",
    flexShrink: 0,
    overflow: "hidden",
    backgroundColor: "#e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: Math.max(14, size * 0.34),
    color: "#334155",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.1)",
  };

  if (loading) {
    return (
      <div
        style={{
          ...boxStyle,
          background:
            "linear-gradient(90deg, #e2e8f0 0%, #f8fafc 50%, #e2e8f0 100%)",
          backgroundSize: "200% 100%",
          animation: "bossAvatarShimmer 1.2s ease-in-out infinite",
        }}
        aria-hidden
      />
    );
  }

  if (url && !imgError) {
    return (
      <img
        src={url}
        alt={`${firstName} ${lastName}`.trim()}
        style={{ ...boxStyle, objectFit: "cover" }}
        onError={() => setImgError(true)}
      />
    );
  }

  return <div style={boxStyle}>{initials || "?"}</div>;
};

export default BossProfileAvatar;
